import {
    type FC,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Icon } from "@onshopfront/components";
import moment from "moment";
import { isDarkMode } from "../DarkMode/DarkModeHooks";

const ARTICLE_STYLE = `
body {
    margin: 0;
    padding: 2rem;
    font-family: 'Roboto', sans-serif;
    box-sizing: border-box;
    overflow: hidden;
}

pre {
    padding: 0.25rem 0.5rem;
    max-width: 100%;
    overflow: auto;
    border-radius: 6px;
    background: hsl(0, 0%, 95%);
}

a {
    cursor: pointer;
}

.wysiwyg-text-align-center {
    text-align: center;
}

.wysiwyg-text-align-left {
    text-align: left;
}

.wysiwyg-text-align-right {
    text-align: right;
}

img {
    max-width: 100%;
}

h1 {
    margin-bottom: 0.5rem;
}

.subtitle {
    margin-top: 0.5rem;
    margin-bottom: 2rem;
}

.subtitle .split {
    display: inline-block;
    margin: 0 0.25rem;
}

.dark-mode a {
    color: #5EBBEB;
}

.dark-mode {
    color: #FFFFFF;
}

.icon {
    width: 1.5rem;
    height: 1.5rem;
}

.note {
    color: #075985;
    background: #E0F2FE;
    padding: 1rem;
    border-color: #38BDF8;
    border-style: solid;
    border-width: 0 0 0 4px;
}

.note .note-header {
    display: flex;
    align-items: center;
    gap: .5rem;
}

.note .icon {
    color: #0284C7;
}

.note p {
    margin-bottom: 0;
}

.node .node-title {
    font-weight: 500;
}

.dark-mode .note {
    background: #082F49;
    border-color: #0284C7;
    color: #E0F2FE;
}

.dark-mode .note .icon {
    color: #7DD3FC;
}

.article-block {
    box-sizing: border-box;
    display: block;
    border-radius: .5rem;
    border: #262626 1px solid;
    padding: 1rem;
    background: white; 
    color: black;
    text-decoration: none;
    transition-property: box-shadow;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
}

.article-block.loading {
    height: 72px;
}

.article-block.error {
    cursor: not-allowed;
    opacity: 0.6;
}

.dark-mode .article-block {
    background: #202020;
    color: white;
    border-color: #404040;
}

.article-block:not(.error):hover {
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
    border-color: #475569;
}

.dark-mode .article-block:hover {
    border-color: #A3A3A3;
}

.article-block .article-block-header {
    display: flex;
    justify-content: between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.article-block .article-block-title {
    font-weight: 500;
    flex-grow: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.article-block .article-block-header .icon {
    transition-property: transform;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
    transform: translateX(0);
}

.article-block:hover .article-block-header .icon {
    transform: translateX(0.5rem);
}

.article-block .article-preview {
    font-size: .875rem;
    line-height: 1rem;
}

.permissions {
    display: flex;
    border: currentColor 1px solid;
    background-color: #F8FAFC1A;
    border-radius: .5rem;
    flex-direction: column;
    font-size: .875rem;
    line-height: 1rem;
}

.permissions .permissions-title {
    text-align: center;
    font-weight: 500;
    padding: 1rem;
}

.permissions .permissions-list {
    padding: 1rem;
    border-color: currentColor;
    border-width: 1px 0 0 0;
    border-style: solid;
    list-style: none;
    margin: 0;
}

.permissions .permissions-list .permissions-list-item {
    display: flex;
    align-items: center;
    padding: 0.25rem;
}

.permissions .permissions-list .permissions-list-item .icon {
    margin-right: 0.5rem;
}

.plan {
    display: flex;
    border: currentColor 1px solid;
    background-color: #F8FAFC1A;
    border-radius: .5rem;
    flex-direction: column;
    font-size: .875rem;
    line-height: 1rem;
}

.plan .plan-title {
    font-weight: 500;
    padding: 1rem 2rem;
}

.plan .plan-list {    
    padding: 1rem;
    gap: .5rem;
    justify-content: space-around;
    flex: 1 1 0%;
    border-color: currentColor;
    border-width: 1px 0 0 0;
    border-style: solid;
}

.plan .plan-list .plan-list-item {
    display: flex;
    align-items: center;
    gap: .5rem;
}

.plan .plan-list .plan-list-item.disabled {
    opacity: 0.6;
}

@media (min-width: 550px) {
    .plan {
        flex-direction: row;
        align-items: center;
    }
    
    .plan .plan-title {
        flex-shrink: 1;
        white-space: nowrap;
    }

    .plan .plan-list {
        display: flex;
        border-width: 0 0 0 1px;
    }
}
`;

const ARTICLE_CODE = `
function onClick(e) {
    e.preventDefault();
    
    window.parent.postMessage({
        type: "REDIRECT",
        url: e.currentTarget.href,
    }, "*");
}

function attach() {
    const links = document.querySelectorAll("body a:not(.external)");
    
    for (let i = 0, l = links.length; i < l; i++) {
        links[i].addEventListener("click", onClick);
    }
}

attach();
`;

const IFRAME_SRCDOC = `<!DOCTYPE HTML>
<html lang="en">
    <head><title>Zendesk article</title></head>
    <body></body>
</html>
`;

interface Props {
    id: string;
    scrollAnchor?: string;
}

interface ArticleType {
    url: string;
    title: string;
    body: string;
    timestamp: string;
}

const BASE_ARTICLE = {
    url      : "",
    title    : "",
    body     : "",
    timestamp: "",
};

export const ZendeskArticle: FC<Props> = ({
    id,
    scrollAnchor,
}) => {
    const frameRef = useRef<HTMLIFrameElement>(null);

    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState(false);
    const [ article, setArticle ] = useState<ArticleType>(BASE_ARTICLE);
    const [ scrollAnchorIndex, setScrollAnchorIndex ] = useState(0);

    const loadArticle = useCallback(async (id: string): Promise<ArticleType> => {
        const { article } = await fetch(`https://support.onshopfront.com/api/v2/help_center/en-us/articles/${id}.json`)
            .then(async response => {
                if(!response.ok) {
                    throw await response.json();
                }

                return response.json();
            });

        const { html_url, body, title, created_at, edited_at } = article;

        return {
            title,
            body,
            url      : html_url,
            timestamp: moment(typeof edited_at === "string" ? edited_at : created_at).fromNow(false),
        };
    }, []);

    const loadDisplayed = useCallback(async (id: string) => {
        setLoading(true);
        setError(false);

        try {
            const article = await loadArticle(id);

            setArticle(article);
        } catch(err) {
            console.error("Error loading article", err);

            setError(true);
        }

        setLoading(false);
    }, [ loadArticle ]);

    useEffect(() => {
        void loadDisplayed(id);
    }, [ id, loadDisplayed ]);

    const handleMessage = useCallback((e: MessageEvent) => {
        if(!e.data.type || e.data.type !== "REDIRECT") {
            return; // Ignore invalid message
        }

        const { url } = e.data;

        if(!url.startsWith("https://support.onshopfront.com/hc")) {
            return; // Ignore invalid URL
        }

        const split = url.split("/");
        const id = split[split.length - 1];

        if(isNaN(parseInt(id))) {
            return; // Ignore invalid URL
        }

        void loadDisplayed(id);
    }, [ loadDisplayed ]);

    useEffect(() => {
        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [ handleMessage ]);

    const resizeObserver = useMemo(() => {
        // Re-measure on document resize (e.g. image loading)
        return new ResizeObserver(entries => {
            if(!entries.length || !frameRef.current) {
                return;
            }

            if(entries[0].borderBoxSize.length > 0) {
                frameRef.current.height = `${entries[0].borderBoxSize[0].blockSize}px`;
            } else {
                frameRef.current.height = `${entries[0].contentRect.height}px`;
            }

            setScrollAnchorIndex(prev => prev + 1);
        });
    }, []);

    useEffect(() => {
        if(!frameRef.current) {
            return;
        }

        // Get iframe document
        const document = frameRef.current.contentDocument as Document;

        document.title = `Zendesk article - ${article.title}`;

        // Attach styles to the head
        const head = document.getElementsByTagName("head")[0];

        head.innerHTML = "";

        const styled = document.createElement("style");

        styled.innerHTML = ARTICLE_STYLE;

        head.appendChild(styled);

        // Attach content to the body
        const body = document.getElementsByTagName("body")[0];

        body.innerHTML = "";

        const title = document.createElement("h1");

        title.innerHTML = article.title;

        body.appendChild(title);

        const subtitle = document.createElement("p");

        subtitle.classList.add("subtitle");

        const timestamp = document.createElement("span");

        timestamp.innerText = article.timestamp;

        subtitle.appendChild(timestamp);

        const split = document.createElement("span");

        split.innerHTML = "&middot;";
        split.classList.add("split");

        subtitle.appendChild(split);

        const link = document.createElement("a");

        link.rel = "noopener noreferrer";
        link.target = "_blank";
        link.href = article.url;
        link.innerText = "Open externally";
        link.classList.add("external");

        subtitle.appendChild(link);

        body.appendChild(subtitle);

        // Add article body
        body.insertAdjacentHTML("beforeend", article.body);

        body.querySelectorAll("body > p").forEach(el => {
            if(el.innerHTML.startsWith("{note}") && el.innerHTML.endsWith("{/note}")) {
                // Note block
                const content = el.innerHTML.slice(6, -7);

                el.outerHTML = `
                    <div class="note">
                        <div class="note-header">
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke-width="1.5" 
                                stroke="currentColor" 
                                class="icon"
                            >
                                <path 
                                    stroke-linecap="round" 
                                    stroke-linejoin="round" 
                                    d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
                                />
                            </svg>
                            <span class="note-title">Note</span>
                        </div>
                        <p>
                            ${content}
                        </p>
                    </div>
                `;
            } else if(el.innerHTML.startsWith("{permissions}") && el.innerHTML.endsWith("{/permissions}")) {
                // Permission block
                const content = el.innerHTML.slice(13, -14).split(/<br\s*\/?>/ig).filter(content => !!content);

                el.outerHTML = `
                    <div class="permissions">
                        <div class="permissions-title">
                            Required permissions
                        </div>
                        <ul class="permissions-list">
                            ${content.map(permission => `
                                <li class="permissions-list-item">
                                    <svg 
                                        xmlns="http://www.w3.org/2000/svg" 
                                        fill="none" 
                                        viewBox="0 0 24 24" 
                                        stroke-width="1.5"
                                        stroke="currentColor" 
                                        class="icon"
                                    >
                                        <path 
                                            stroke-linecap="round" 
                                            stroke-linejoin="round" 
                                            d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
                                        />
                                    </svg>
                                    <span>${permission}</span>
                                </li>
                            `).join("\r\n")}
                        </ul>
                    </div>
                `;
            } else if(el.innerHTML.startsWith("{plan}") && el.innerHTML.endsWith("{/plan}")) {
                // Plan block
                const content = el.innerHTML.slice(6, -7).split(/<br\s*\/?>/ig).filter(content => !!content);

                const list = content.map(plan => {
                    const split = plan.trim().split(":");
                    const enabled = split[1] === "true";

                    return `
                        <div class="plan-list-item${enabled ? "" : " disabled"}">
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                fill="none" 
                                viewBox="0 0 24 24" 
                                stroke-width="1.5" 
                                stroke="currentColor" 
                                class="icon"
                            >
                                <path 
                                    stroke-linecap="round" 
                                    stroke-linejoin="round"
                                    d="${(
                                        enabled ?
                                            "M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" :
                                            "m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                                    )}"
                                />
                            </svg>
                            <div>${split[0]}</div>
                        </div>
                    `;
                }).join("");

                el.outerHTML = `
                    <div class="plan">
                        <div class="plan-title">
                            Shopfront Plan
                        </div>
                        <div class="plan-list">
                            ${list}
                        </div>
                    </div>
                `;
            } else if(el.innerHTML.startsWith("{addon}") && el.innerHTML.endsWith("{/addon}")) {
                // Addon block
                const content = el.innerHTML.slice(7, -8).split(/<br\s*\/?>/ig).filter(content => !!content);

                el.outerHTML = `
                    <div class="plan">
                        <div class="plan-title">
                            Addon Required
                        </div>
                        <div class="plan-list">
                            ${content.map(plan => `
                                <div class="plan-list-item">
                                    <div>
                                        <svg 
                                            xmlns="http://www.w3.org/2000/svg" 
                                            fill="none" 
                                            viewBox="0 0 24 24" 
                                            stroke-width="1.5" 
                                            stroke="currentColor" 
                                            class="icon"
                                        >
                                            <path 
                                                stroke-linecap="round" 
                                                stroke-linejoin="round"
                                                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                                            />
                                        </svg>
                                    </div>
                                    <div>${plan}</div>
                                </div>
                            `).join("")}
                        </div>
                    </div>
                `;
            } else if(el.innerHTML.startsWith("{article}") && el.innerHTML.endsWith("{/article}")) {
                const id = el.innerHTML.slice(9, -10);

                const articleLink = document.createElement("a");

                articleLink.href = "#";
                articleLink.innerHTML = `
                    <div class="article-block-header">
                        <div class="article-block-title">
                            Loading Article...
                        </div>
                    </div>
                `;

                articleLink.classList.add("article-block", "loading");

                el.replaceChildren(articleLink);

                loadArticle(id)
                    .then(article => {
                        const preview = article
                            .body
                            .replace(/<\/?p>/g, "")
                            .replace(/\{[^}]+}.*?\{\/[^}]+}/g, "")
                            .trim()
                            .slice(0, 100);

                        articleLink.classList.remove("loading");

                        articleLink.href = article.url;
                        articleLink.innerHTML = `
                            <div class="article-block-header">
                                <div class="article-block-title">
                                    ${article.title}
                                </div>
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke-width="1.5" 
                                    stroke="currentColor" 
                                    class="icon"
                                >
                                    <path 
                                        stroke-linecap="round" 
                                        stroke-linejoin="round" 
                                        d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3"
                                    />
                                </svg>
                            </div>
                            <div class="article-preview">
                                ${preview}...
                            </div>
                        `;
                    })
                    .catch(err => {
                        console.error("Error loading article", err);

                        const articleError = document.createElement("div");

                        articleError.innerHTML = `
                            <div class="article-block-header">
                                <div class="article-block-title">
                                    Error
                                </div>
                            </div>
                            <div class="article-preview">
                                There was an error loading article details
                            </div>
                        `;

                        articleError.classList.add("article-block", "error");

                        el.replaceChildren(articleError);
                    });
            }
        });

        const code = document.createElement("script");

        code.innerHTML = ARTICLE_CODE;

        body.appendChild(code);

        if(isDarkMode()) {
            body.classList.add("dark-mode", "dark");
        }

        if(typeof scrollAnchor === "string") {
            requestAnimationFrame(() => {
                const scrollTarget = document.getElementById(scrollAnchor);

                if(scrollTarget) {
                    let loaded = 0;
                    const images = body.querySelectorAll("img");

                    const scrollIntoView = () => {
                        loaded++;

                        if(loaded !== images.length) {
                            return;
                        }

                        setScrollAnchorIndex(prev => prev + 1);
                    };

                    images.forEach(img => {
                        img.addEventListener("load", scrollIntoView, { once: true });
                    });

                    if(!images.length) {
                        setScrollAnchorIndex(prev => prev + 1);
                    }
                }
            });
        }

        resizeObserver.observe(body);

        return () => {
            resizeObserver.unobserve(body);
        };
    }, [ article, scrollAnchor, loadArticle, resizeObserver ]);

    useEffect(() => {
        if(!scrollAnchor) {
            return;
        }

        requestAnimationFrame(() => {
            frameRef.current?.contentDocument?.getElementById(scrollAnchor)?.scrollIntoView(true);
        });
    }, [ scrollAnchor, scrollAnchorIndex ]);

    return (
        <div className="w-full h-full">
            {loading && (
                <div className="h-full text-xl flex justify-center items-center">
                    <Icon
                        icon={[ "fal", "spinner-third" ]}
                        className="mr-4"
                        spin
                    />
                    Loading Article...
                </div>
            )}
            {error && (
                <div className="h-full flex flex-col justify-center items-center">
                    <Icon
                        icon={[ "fal", "exclamation-triangle" ]}
                        className="text-4xl mb-2 text-red-700 dark:text-red-300"
                    />
                    <div>
                        Error loading Zendesk Article
                    </div>
                </div>
            )}
            <iframe
                ref={frameRef}
                srcDoc={IFRAME_SRCDOC}
                className={"w-full" + (
                    (loading || error) ?
                        " hidden" :
                        ""
                )}
            />
        </div>
    );
};
